/**
 * AdminConfigPage - View and edit recommendation config
 *
 * Drives:
 * - GET /api/admin/config/recommendation?key=default
 * - PUT /api/admin/config/recommendation
 * - GET /api/admin/config/recommendation/list
 */

import { useState, useEffect } from 'react';
import { RefreshCw, Save, AlertTriangle, CheckCircle2, Eye, Edit2 } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

interface ConfigRow {
  key: string;
  version: string;
  config: Record<string, unknown>;
  updated_at: string;
}

interface ConfigListResponse {
  configs: Array<{ key: string; version: string; updated_at: string }>;
}

export function AdminConfigPage() {
  const { session } = useAuth();

  // State
  const [configList, setConfigList] = useState<ConfigListResponse['configs']>([]);
  const [selectedKey, setSelectedKey] = useState('default');
  const [config, setConfig] = useState<ConfigRow | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editJson, setEditJson] = useState('');
  const [editVersion, setEditVersion] = useState('');

  // Loading/error state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch config list
  const fetchConfigList = async () => {
    if (!session?.access_token) return;

    try {
      const data = await apiFetch<ConfigListResponse>(
        '/admin/config/recommendation/list',
        { token: session.access_token }
      );
      setConfigList(data.configs || []);
    } catch (err) {
      console.error('Failed to fetch config list:', err);
    }
  };

  // Fetch specific config
  const fetchConfig = async (key: string) => {
    if (!session?.access_token) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch<ConfigRow>(
        `/admin/config/recommendation?key=${encodeURIComponent(key)}`,
        { token: session.access_token }
      );
      setConfig(data);
      setEditJson(JSON.stringify(data.config, null, 2));
      setEditVersion(data.version);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch config');
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  // Save config
  const saveConfig = async () => {
    if (!session?.access_token) return;

    // Validate JSON
    let parsedConfig: Record<string, unknown>;
    try {
      parsedConfig = JSON.parse(editJson);
    } catch {
      setError('Invalid JSON format');
      return;
    }

    // Validate version format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(editVersion)) {
      setError('Version must be in YYYY-MM-DD format');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await apiFetch('/admin/config/recommendation', {
        method: 'PUT',
        token: session.access_token,
        body: {
          key: selectedKey,
          version: editVersion,
          config: parsedConfig,
        },
      });

      setSuccess('Config saved successfully');
      setEditMode(false);

      // Refresh
      await fetchConfig(selectedKey);
      await fetchConfigList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchConfigList();
    fetchConfig(selectedKey);
  }, [session?.access_token]);

  // Handle key change
  const handleKeyChange = (key: string) => {
    setSelectedKey(key);
    setEditMode(false);
    fetchConfig(key);
  };

  return (
    <AdminLayout
      title="Recommendation Config"
      description="View and edit recommendation system configuration"
    >
      {/* Config Key Selection */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex gap-2">
          {configList.map((item) => (
            <button
              key={item.key}
              onClick={() => handleKeyChange(item.key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                selectedKey === item.key
                  ? 'bg-teal-500 text-white'
                  : 'bg-[#1A1F26] text-slate-300 hover:text-white'
              }`}
            >
              {item.key}
            </button>
          ))}
        </div>

        <button
          onClick={() => fetchConfig(selectedKey)}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 bg-[#1A1F26] rounded-lg hover:text-white transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg mb-4">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle2 size={16} />
            <span>{success}</span>
          </div>
        </div>
      )}

      {/* Config Display/Edit */}
      {config && (
        <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-400">
                Key: <span className="text-white">{config.key}</span> ·
                Version: <span className="text-white">{config.version}</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Last updated: {new Date(config.updated_at).toLocaleString()}
              </p>
            </div>

            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white transition-colors"
              >
                <Edit2 size={14} />
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditJson(JSON.stringify(config.config, null, 2));
                    setEditVersion(config.version);
                    setError(null);
                  }}
                  className="px-3 py-2 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveConfig}
                  disabled={saving}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-teal-500 rounded-lg hover:bg-teal-400 disabled:opacity-50 transition-colors"
                >
                  {saving ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  Save
                </button>
              </div>
            )}
          </div>

          {/* Version Input (edit mode) */}
          {editMode && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Version (YYYY-MM-DD)
              </label>
              <input
                type="text"
                value={editVersion}
                onChange={(e) => setEditVersion(e.target.value)}
                placeholder="2026-01-17"
                className="w-64 px-3 py-2 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white font-mono text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
          )}

          {/* Config JSON */}
          {editMode ? (
            <textarea
              value={editJson}
              onChange={(e) => setEditJson(e.target.value)}
              rows={30}
              className="w-full px-4 py-3 bg-[#0F1419] border border-white/[0.12] rounded-lg text-white font-mono text-sm focus:outline-none focus:border-teal-500"
            />
          ) : (
            <div className="relative">
              <div className="absolute top-2 right-2">
                <Eye size={14} className="text-slate-500" />
              </div>
              <pre className="p-4 bg-[#0F1419] rounded-lg text-sm text-slate-300 font-mono overflow-x-auto max-h-[600px]">
                {JSON.stringify(config.config, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && !config && (
        <div className="p-8 bg-[#1A1F26] rounded-lg border border-white/[0.08] text-center">
          <RefreshCw size={24} className="mx-auto text-slate-600 animate-spin mb-3" />
          <p className="text-slate-400">Loading config...</p>
        </div>
      )}

      {/* Safety Notice */}
      <div className="mt-6 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
        <h4 className="font-medium text-orange-400 mb-2">Safety Notice</h4>
        <p className="text-sm text-slate-300">
          Changes to recommendation config affect system behavior immediately.
          Always increment the version date when making changes.
          Invalid JSON or config structure will be rejected.
        </p>
      </div>
    </AdminLayout>
  );
}
